class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

if (!customElements.get("anchored-popover-component")) {
  customElements.define(
    "anchored-popover-component",
    class AnchoredPopoverComponent extends HTMLElement {
      constructor() {
        super();

        this.interactionDelay = 200;
        this.popoverTrigger = null;
        this.handleResize = debounce(() => {
          if (this.popoverElement?.matches(":popover-open")) {
            this.popoverElement.hidePopover();
          }
        }, 100);
        this.onTriggerEnter = this.onTriggerEnter.bind(this);
        this.onTriggerLeave = this.onTriggerLeave.bind(this);
        this.onPopoverEnter = this.onPopoverEnter.bind(this);
        this.onPopoverLeave = this.onPopoverLeave.bind(this);
        this.beforeToggleHandler = this.beforeToggleHandler.bind(this);
      }

      connectedCallback() {
        this.trigger = this.querySelector('[data-ref="trigger"]');
        this.popoverElement = this.querySelector('[data-ref="popover"]');

        if (!this.trigger || !this.popoverElement) return;

        if (this.dataset.closeOnResize !== undefined) {
          this.popoverElement.addEventListener(
            "beforetoggle",
            this.beforeToggleHandler
          );
        }

        if (this.dataset.hoverTriggered !== undefined) {
          this.trigger.addEventListener("pointerenter", this.onTriggerEnter);
          this.trigger.addEventListener("pointerleave", this.onTriggerLeave);
          this.popoverElement.addEventListener("pointerenter", this.onPopoverEnter);
          this.popoverElement.addEventListener("pointerleave", this.onPopoverLeave);
        }

        if (!CSS.supports("position-anchor: --trigger")) {
          this.popoverElement.addEventListener("beforetoggle", () => {
            this.updatePosition();
          });
          this.updatePosition();
        }
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.handleResize);
        this.trigger?.removeEventListener("pointerenter", this.onTriggerEnter);
        this.trigger?.removeEventListener("pointerleave", this.onTriggerLeave);
        this.popoverElement?.removeEventListener("pointerenter", this.onPopoverEnter);
        this.popoverElement?.removeEventListener("pointerleave", this.onPopoverLeave);
        this.popoverElement?.removeEventListener(
          "beforetoggle",
          this.beforeToggleHandler
        );
      }

      updatedCallback() {
        this.updatePosition();
      }

      beforeToggleHandler(event) {
        const nextState = event.newState;
        window[nextState === "open" ? "addEventListener" : "removeEventListener"](
          "resize",
          this.handleResize
        );
      }

      onTriggerEnter() {
        this.trigger.dataset.hoverActive = "true";

        if (!this.popoverElement.matches(":popover-open")) {
          this.popoverTrigger = window.setTimeout(() => {
            if (this.trigger.matches("[data-hover-active]")) {
              this.popoverElement.showPopover();
            }
          }, this.interactionDelay);
        }
      }

      onTriggerLeave() {
        delete this.trigger.dataset.hoverActive;

        if (this.popoverTrigger) {
          clearTimeout(this.popoverTrigger);
        }

        if (this.popoverElement.matches(":popover-open")) {
          this.popoverTrigger = window.setTimeout(() => {
            this.popoverElement.hidePopover();
          }, this.interactionDelay);
        }
      }

      onPopoverEnter() {
        if (this.popoverTrigger) {
          clearTimeout(this.popoverTrigger);
        }
      }

      onPopoverLeave() {
        this.popoverTrigger = window.setTimeout(() => {
          this.popoverElement.hidePopover();
        }, this.interactionDelay);
      }

      updatePosition() {
        if (!this.trigger || !this.popoverElement) return;

        const triggerRect = this.trigger.getBoundingClientRect();

        this.popoverElement.style.setProperty("--anchor-top", `${triggerRect.top}`);
        this.popoverElement.style.setProperty(
          "--anchor-right",
          `${window.innerWidth - triggerRect.right}`
        );
        this.popoverElement.style.setProperty(
          "--anchor-bottom",
          `${window.innerHeight - triggerRect.bottom}`
        );
        this.popoverElement.style.setProperty("--anchor-left", `${triggerRect.left}`);
        this.popoverElement.style.setProperty(
          "--anchor-height",
          `${triggerRect.height}`
        );
        this.popoverElement.style.setProperty(
          "--anchor-width",
          `${triggerRect.width}`
        );
      }
    }
  );
}

if (!customElements.get("cart-volume-pricing")) {
  customElements.define(
    "cart-volume-pricing",
    class CartVolumePricing extends HTMLElement {
      constructor() {
        super();

        this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
      }

      connectedCallback() {
        this.quantityInput = this.closest(".cart-item")?.querySelector(
          ".quantity__input"
        );

        this.closest(".cart-item")?.addEventListener("input", this.onQuantityUpdate);
        this.closest(".cart-item")?.addEventListener("change", this.onQuantityUpdate);

        this.updateActiveTier(
          Number(this.dataset.currentQuantity || this.quantityInput?.value || 0)
        );

        const anchoredPopover = this.closest("anchored-popover-component");
        if (anchoredPopover?.updatedCallback) {
          anchoredPopover.updatedCallback();
        }
      }

      disconnectedCallback() {
        this.closest(".cart-item")?.removeEventListener("input", this.onQuantityUpdate);
        this.closest(".cart-item")?.removeEventListener("change", this.onQuantityUpdate);
      }

      onQuantityUpdate(event) {
        if (!(event.target instanceof HTMLInputElement)) return;
        if (!event.target.classList.contains("quantity__input")) return;

        this.updateActiveTier(Number(event.target.value || 0));
      }

      updateActiveTier(quantity) {
        const rows = this.querySelectorAll(".volume-pricing__row[data-quantity]");

        let activeTier = null;

        rows.forEach((row) => {
          row.classList.remove("volume-pricing__row--active");

          if (quantity >= Number(row.dataset.quantity || 0)) {
            activeTier = row;
          }
        });

        activeTier?.classList.add("volume-pricing__row--active");
      }
    }
  );
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    if (document.querySelector(".cart-shipping")) {
      this.minSpend = document.querySelector(".cart-shipping").dataset.minSpend;
      this.minTotal = Math.round(this.minSpend * (Shopify.currency.rate || 1));
      this.cartShipping();
    }

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  cartShipping() {
    let progressPrev = getComputedStyle(
      document.querySelector(".cart-shipping__progress-current")
    ).getPropertyValue("width");
    document.documentElement.style.setProperty("--progress-prev", progressPrev);

    this.total = document.querySelector(".cart-shipping").dataset.total;
    this.progress = (this.total / this.minTotal) * 100;
    if (this.progress > 100) this.progress = 100;

    if (this.minTotal > this.total) {
      let amount = this.minTotal - this.total;
      let message = document
        .querySelector(".cart-shipping")
        .dataset.message.replace("||amount||", formatMoney(amount));
      document.querySelector(".cart-shipping__message_default").innerText =
        message;
      document
        .querySelector(".cart-shipping__message_success")
        .classList.remove("active");
      document
        .querySelector(".cart-shipping__message_default")
        .classList.add("active");
    } else {
      document
        .querySelector(".cart-shipping__message_default")
        .classList.remove("active");
      document
        .querySelector(".cart-shipping__message_success")
        .classList.add("active");
    }

    document.querySelector(".cart-shipping__progress-current").style.width =
      this.progress + "%";
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );

    const clearCartButton = document.getElementById("clear-cart");
    if (clearCartButton) {
      clearCartButton.addEventListener("click", () => {
        this.clearAllItems();
      });
    }
  }

  clearAllItems() {
    fetch("/cart/clear.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Cart cleared", data);
        window.location.reload();
      })
      .catch((error) => {
        console.error("Error clearing cart:", error);
      });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    const sections = [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents-totals",
      },
    ];

    const shippingSection = document.getElementById("main-cart-shipping");

    return shippingSection
      ? [
          ...sections,
          {
            id: "main-cart-shipping",
            section: shippingSection.dataset.id || null,
            selector: ".js-contents-shipping",
          },
        ]
      : sections;
  }

  updateQuantity(line, quantity, name) {
    if (!this.validateQuantity(line)) {
      return;
    }

    this.enableLoading(line);
    this.querySelectorAll(".quantity__button").forEach((button) =>
      button.classList.add("disabled")
    );

    if (
      document.querySelectorAll(
        '.card--product card__add-to-cart button[name="add"]'
      )
    ) {
      document
        .querySelectorAll(
          '.card--product .card__add-to-cart button[name="add"]'
        )
        .forEach((button) => {
          button.setAttribute("aria-disabled", false);
          if (button.querySelector("span")) {
            button.querySelector("span").classList.remove("hidden");
            button.querySelector(".sold-out-message").classList.add("hidden");
          }
        });
    }

    if (document.querySelector(".cart-shipping")) {
      let progressPrev = getComputedStyle(
        document.querySelector(".cart-shipping__progress-current")
      ).getPropertyValue("width");
      document.documentElement.style.setProperty(
        "--progress-prev",
        progressPrev
      );
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);

          // dispatch cart:error
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                source: this.dataset.source,
                productVariantId: items[line - 1].dataset.variantId || line,
                errors: parsedState.errors,
                message: parsedState.errors,
              },
            })
          );
          return;
        }

        // dispatch line-item:change for the modified element
        document.dispatchEvent(
          new CustomEvent("line-item:change", {
            detail: {
              lineItem: parsedState.items[line - 1] || null,
              cart: parsedState,
              sectionId: this.dataset.source,
            },
          })
        );

        this.classList.toggle("is-empty", parsedState.item_count === 0);

        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: {
              cart: parsedState,
              sectionId: this.dataset.source,
            },
          })
        );

        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      })
      .catch(() => {
        const items = document.querySelectorAll(".cart-item");
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        this.syncQuantityButtonsState();
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            detail: {
              source: this.dataset.source,
              productVariantId: items[line - 1]?.dataset?.variantId || line,
              errors: window.cartStrings.error,
              message: window.cartStrings.error,
            },
          })
        );
      })
      .finally(() => {
        this.syncQuantityButtonsState();
        if (document.querySelector(".cart-shipping")) {
          this.cartShipping();
        }
        this.disableLoading(line);
        const closeButton = document.querySelector(".cart__note-close-button");
        if (closeButton) {
          closeButton.addEventListener("click", function () {
            document
              .getElementById("Details-CartDrawer")
              .removeAttribute("open");
          });
        }
      });
  }

  syncQuantityButtonsState() {
    this.querySelectorAll("quantity-input").forEach((quantityInput) => {
      if (typeof quantityInput.validateQtyRules === "function") {
        quantityInput.validateQtyRules();
      } else {
        quantityInput
          .querySelectorAll(".quantity__button")
          .forEach((button) => button.classList.remove("disabled"));
      }
    });
  }

  validateQuantity(line) {
    const items = this.querySelectorAll(".cart-item");
    const input = items[line - 1]?.querySelector(".quantity__input");

    if (!input) {
      return false;
    }

    return input.reportValidity();
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.style.display = "flex";
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-discount-code")) {
  customElements.define(
    "cart-discount-code",
    class CartDiscountCode extends HTMLElement {
      constructor() {
        super();

        this.activeFetch = null;
      }

      connectedCallback() {
        this.#initRefs();
        this.form = this.querySelector("form");
        this.input = this.querySelector('input[name="discount"]');
        this.submitBtn = this.querySelector('button[type="submit"]');

        this.form?.addEventListener("submit", this.applyDiscount);
        this.addEventListener("click", this.removeDiscount);
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this.applyDiscount);
        this.removeEventListener("click", this.removeDiscount);

        if (this.activeFetch) {
          this.activeFetch.abort();
          this.activeFetch = null;
        }
      }

      #initRefs() {
        this.refs = {};

        for (const element of this.querySelectorAll("[ref]")) {
          const name = element.getAttribute("ref");
          if (name && element instanceof HTMLElement) {
            this.refs[name] = element;
          }
        }
      }

      #createAbortController() {
        if (this.activeFetch) {
          this.activeFetch.abort();
        }

        const abortController = new AbortController();
        this.activeFetch = abortController;
        return abortController;
      }

      #requestedSections() {
        const sectionIds = new Set();
        const primarySectionId = this.dataset.sectionId;
        const pageSectionId = document.getElementById("main-cart-items")?.dataset.id;

        if (primarySectionId) {
          sectionIds.add(primarySectionId);
        }

        if (primarySectionId !== "cart-drawer" && document.querySelector("cart-drawer")) {
          sectionIds.add("cart-drawer");
        }

        if (primarySectionId === "cart-drawer" && pageSectionId) {
          sectionIds.add(pageSectionId);
        }

        return Array.from(sectionIds);
      }

      applyDiscount = async (event) => {
        const {
          cartDiscountError,
          cartDiscountErrorDiscountCode,
          cartDiscountErrorShipping,
        } = this.refs;

        event.preventDefault();
        event.stopPropagation();

        const form = event.target;

        if (
          !(form instanceof HTMLFormElement) ||
          !(this.input instanceof HTMLInputElement) ||
          typeof this.dataset.sectionId !== "string"
        ) {
          return;
        }

        const discountCodeValue = this.input.value.trim();
        if (!discountCodeValue) return;

        const abortController = this.#createAbortController();
        const btnLoader = this.submitBtn?.querySelector(".spinner");

        this.submitBtn?.classList.add("loading");

        try {
          const existingDiscounts = this.#existingDiscounts();
          if (
            existingDiscounts.some(
              (code) => code.toUpperCase() === discountCodeValue.toUpperCase()
            )
          ) {
            return;
          }

          cartDiscountError?.classList.add("hidden");
          cartDiscountErrorDiscountCode?.classList.add("hidden");
          cartDiscountErrorShipping?.classList.add("hidden");

          const sections = this.#requestedSections();
          const body = JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(","),
            sections,
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            ...{ body },
            signal: abortController.signal,
          });

          const data = await response.json();

          if (!this.#checkApplicableCode(data.discount_codes, discountCodeValue)) {
            return;
          }

          const sourceSection = this.#getSourceSection(
            data.sections?.[this.dataset.sectionId],
            this.dataset.sectionId
          );

          if (
            sourceSection &&
            !this.#checkShippingDiscountCode(
              data.discount_codes,
              discountCodeValue,
              sourceSection
            )
          ) {
            return;
          }

          this.#updateRequestedSections(data.sections, sections);
        } catch (error) {
          console.error("Failed to apply discount:", error);
        } finally {
          this.activeFetch = null;
          this.submitBtn?.classList.remove("loading");
          btnLoader?.classList.remove("hidden");
        }
      };

      removeDiscount = async (event) => {
        if (
          !(event.target instanceof HTMLElement) ||
          typeof this.dataset.sectionId !== "string"
        ) {
          return;
        }

        const pill = event.target.closest(".cart-discount-code__pill");
        if (!(pill instanceof HTMLLIElement)) return;

        event.preventDefault();
        event.stopPropagation();

        const discountCode = pill.dataset.discountCode;
        if (!discountCode) return;

        const existingDiscounts = this.#existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        const abortController = this.#createAbortController();
        const btnLoader = this.submitBtn?.querySelector(".spinner");

        this.submitBtn?.classList.add("loading");

        try {
          const sections = this.#requestedSections();
          const body = JSON.stringify({
            discount: existingDiscounts.join(","),
            sections,
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            ...{ body },
            signal: abortController.signal,
          });

          const data = await response.json();

          this.#updateRequestedSections(data.sections, sections);
        } catch (error) {
          console.error("Failed to remove discount:", error);
        } finally {
          this.activeFetch = null;
          this.submitBtn?.classList.remove("loading");
          btnLoader?.classList.remove("hidden");
        }
      };

      #handleDiscountError(type) {
        const {
          cartDiscountError,
          cartDiscountErrorDiscountCode,
          cartDiscountErrorShipping,
        } = this.refs;

        const target =
          type === "discount_code"
            ? cartDiscountErrorDiscountCode
            : cartDiscountErrorShipping;

        cartDiscountError?.classList.remove("hidden");
        target?.classList.remove("hidden");
      }

      #existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll(".cart-discount-code__pill");

        for (const pill of discountPills) {
          if (
            pill instanceof HTMLLIElement &&
            typeof pill.dataset.discountCode === "string"
          ) {
            discountCodes.push(pill.dataset.discountCode);
          }
        }

        return discountCodes;
      }

      #checkApplicableCode(cartDiscountCodes, discountCodeValue) {
        if (
          cartDiscountCodes.find((discount) => {
            return (
              discount.code === discountCodeValue && discount.applicable === false
            );
          })
        ) {
          this.input.value = "";
          this.#handleDiscountError("discount_code");
          return false;
        }

        return true;
      }

      #checkShippingDiscountCode(
        cartDiscountCodes,
        discountCodeValue,
        sourceSection
      ) {
        const discountCodes =
          sourceSection?.querySelectorAll(".cart-discount-code__pill") || [];
        const existingDiscounts = this.#existingDiscounts();

        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement ? element.dataset.discountCode : null
          )
          .filter(Boolean);

        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          cartDiscountCodes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError("shipping");
          this.input.value = "";
          return false;
        }

        return true;
      }

      #getSourceSection(sectionHtml, sectionId) {
        if (!sectionHtml || !sectionId) return null;

        const parsedHtml = new DOMParser().parseFromString(sectionHtml, "text/html");

        if (sectionId === "cart-drawer") {
          return parsedHtml.getElementById("CartDrawer");
        }

        return parsedHtml.getElementById(`shopify-section-${sectionId}`);
      }

      #updateRequestedSections(sectionsHtml, sectionIds) {
        if (!sectionsHtml) return;

        for (const sectionId of sectionIds) {
          const sectionHtml = sectionsHtml[sectionId];
          if (!sectionHtml) continue;

          if (sectionId === "cart-drawer") {
            this.#updateDrawerSection(sectionHtml);
            continue;
          }

          this.#updateCartPageSection(sectionHtml, sectionId);
        }
      }

      #updateDrawerSection(sectionHtml) {
        const sourceSection = this.#getSourceSection(sectionHtml, "cart-drawer");
        const sourceInner = sourceSection?.querySelector(".drawer__inner");
        const targetInner = document.querySelector("#CartDrawer .drawer__inner");

        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }
      }

      #updateCartPageSection(sectionHtml, sectionId) {
        const sourceSection = this.#getSourceSection(sectionHtml, sectionId);
        if (!sourceSection) return;

        const regions = [
          { id: "main-cart-items", selector: ".js-contents" },
          { id: "main-cart-footer", selector: ".js-contents-totals" },
          { id: "main-cart-shipping", selector: ".js-contents-shipping" },
        ];

        for (const region of regions) {
          const sourceRegion = sourceSection.querySelector(`#${region.id} ${region.selector}`);
          const targetRegion = document.querySelector(`#${region.id} ${region.selector}`);

          if (sourceRegion && targetRegion) {
            targetRegion.innerHTML = sourceRegion.innerHTML;
          }
        }
      }
    }
  );
}

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
